import { useState } from "react";
import { Button, Confirm, Empty, EnvBadge, ErrorNotice, Field, Input, Loading, Panel, SecretReveal, StatusBadge, useToast } from "../../components/ui";
import { developerApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { useClient } from "../../lib/session";
import { formatDateTime, relativeTime } from "../../lib/time";
import { ALL_SCOPES, DEFAULT_SCOPES, POWER_SCOPES, type ApiKey } from "../../lib/types";
import type { GameTabProps } from "./Game";

const SCOPE_HELP: Record<string, string> = {
  "bank:read": "Read banks, currencies, the clock and reconciliation",
  "bank:write": "Create banks and custom currencies",
  "account:read": "Read accounts, balances, history and name enquiry",
  "account:write": "Map players, open, freeze and close accounts",
  "transfer:create": "Transfers and holds between player accounts",
  "funding:create": "Create money: credit players from the issuance account",
  "transaction:reverse": "Reverse posted transactions",
  "limits:write": "Change limits and the game clock",
  "webhooks:read": "Read webhook endpoints, the event log and deliveries",
  "webhooks:write": "Add, change and test webhook endpoints; redeliver events",
  "merchants:write": "Create and change merchants (shops) and their accounts",
  "cards:read": "Read cards and card payments",
  "cards:write": "Issue cards; block, unblock, report lost and close them",
  "cards:charge": "Authorise, capture, release and refund card payments",
  "deposits:write": "Book, change and break fixed deposits",
  "loans:write": "Make loans, repay, pay off and write off",
};

export function GameKeys({ game, env }: GameTabProps) {
  const api = developerApi(useClient());
  const toast = useToast();
  const keys = useQuery(`dev:keys:${game.id}`, () => api.keys(game.id));
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<string[]>(DEFAULT_SCOPES);
  const [secret, setSecret] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const issue = useAction(() => api.issueKey(game.id, { environment: env, label: label.trim() || `${env} key`, scopes }));
  const revoke = useAction(async (k: ApiKey) => {
    await api.revokeKey(game.id, k.id);
    return true;
  });
  const rows = (keys.data?.data ?? []).filter((k) => k.environment === env);
  const toggle = (s: string) => setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <div className="stack">
      <Panel title={<>Issue a <EnvBadge env={env} /> key</>} sub="Keys go on your game servers only, never in a game client or web page. Give each server just the scopes it needs.">
        {secret ? (
          <SecretReveal secret={secret} what="API key">
            <Button onClick={() => setSecret(null)}>Done</Button>
          </SecretReveal>
        ) : (
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const r = await issue.run();
              if (r) {
                setSecret(r.api_key.secret ?? "");
                setLabel("");
                invalidate(`dev:keys:${game.id}`);
                toast("Key issued");
              }
            }}
          >
            <Field label="Label"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="game-server" maxLength={80} /></Field>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="small" style={{ fontWeight: 500, marginBottom: 8 }}>Scopes</legend>
              <div className="grid-2" style={{ gap: 8 }}>
                {ALL_SCOPES.map((s) => (
                  <label key={s} className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                    <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} style={{ marginTop: 4 }} />
                    <span>
                      <code className="mono">{s}</code>
                      <span className="cell-sub" style={{ display: "block" }}>{SCOPE_HELP[s]}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {scopes.some((s) => POWER_SCOPES.has(s)) && (
              <div className="notice warn">This key can create or reverse money. Keep it on a separate, locked-down service such as your payroll job.</div>
            )}
            <ErrorNotice error={issue.error} />
            <div><Button type="submit" variant="primary" busy={issue.pending} disabled={scopes.length === 0}>Issue key</Button></div>
          </form>
        )}
      </Panel>

      <Panel title="Keys" sub="You can have several active keys, so rotate by issuing a new one, deploying it, then revoking the old one." flush>
        <ErrorNotice error={keys.error} onRetry={keys.reload} />
        {keys.loading && !keys.data && <Loading />}
        {keys.data && rows.length === 0 && <Empty title={`No ${env} keys yet`}>Issue one above for your game server.</Empty>}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Key</th><th>Scopes</th><th>Last used</th><th>Created</th><th>Status</th><th /></tr></thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.id}>
                    <td><div className="cell-title">{k.label}</div><div className="cell-sub mono">{k.prefix}</div></td>
                    <td><div className="scope-list">{k.scopes.map((s) => <code key={s} className={POWER_SCOPES.has(s) ? "power" : ""}>{s}</code>)}</div></td>
                    <td>{relativeTime(k.last_used_at)}</td>
                    <td>{formatDateTime(k.created_at)}</td>
                    <td><StatusBadge status={k.revoked_at ? "revoked" : "active"} /></td>
                    <td className="num">{!k.revoked_at && <Button size="small" variant="danger" onClick={() => setRevoking(k)}>Revoke</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Confirm
        open={!!revoking}
        title="Revoke this key?"
        body={<>Servers using <span className="mono">{revoking?.prefix}</span> are refused from their next request. This can't be undone.</>}
        confirmLabel="Revoke key"
        danger
        busy={revoke.pending}
        error={revoke.error}
        onConfirm={async () => {
          if (revoking && (await revoke.run(revoking))) {
            setRevoking(null);
            invalidate(`dev:keys:${game.id}`);
            toast("Key revoked");
          }
        }}
        onClose={() => setRevoking(null)}
      />
    </div>
  );
}
